import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { createContext, forwardRef, useContext, useId, useMemo } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { Icon } from '../icons/icons';
import styles from './ChoiceGroup.module.scss';

/** `card`: each option a bordered 48px card; `list`: plain rows. */
export type ChoiceGroupAppearance = 'card' | 'list';

/** Option columns from 768px; one column below it. */
export type ChoiceGroupColumns = 1 | 2;
export interface ChoiceGroupProps {
  /** The group's name, rendered as the fieldset's `<legend>`. */
  legend: ReactNode;
  /** Hide the legend visually; its text stays in the DOM as the name. */
  legendHidden?: boolean;
  /**
   * Draw the required marker after the legend (hidden from assistive
   * technology) and mark every radio Option `required`, which makes the
   * radio group required. A checkbox group's options take `required` from
   * the caller: on a checkbox it means that box must be checked.
   */
  required?: boolean;
  /** The error message: `role="alert"`, named by the fieldset's `aria-describedby`. */
  error?: string;
  /** Defaults to `card`. */
  appearance?: ChoiceGroupAppearance;
  /** Defaults to 1. */
  columns?: ChoiceGroupColumns;
  children: ReactNode;
  className?: never;
  style?: never;
}
export type ChoiceGroupOptionProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'style' | 'type' | 'children'> & {
  type: 'radio' | 'checkbox';
  /** The option's text, inside the `<label>` that wraps the input. */
  label: ReactNode;
  /** A second line under the label, in the secondary text. */
  description?: ReactNode;
  /** A decorative icon before the label (aria-hidden). */
  icon?: ReactNode;
  className?: never;
  style?: never;
};
interface ChoiceGroupContextValue {
  appearance: ChoiceGroupAppearance;
  invalid: boolean;
  required: boolean;
}
const ChoiceGroupContext = createContext<ChoiceGroupContextValue | null>(null);

/**
 * A radio or checkbox group: a `<fieldset>` named by its `<legend>`, the
 * options, and an error wired as FormGroup wires one. Each option is a
 * `ChoiceGroup.Option`, which takes the group's appearance, `aria-invalid`
 * while the group has an error, and `required` (a radio) while the group is
 * required; an Option's own value wins.
 */
function ChoiceGroupRoot(t0) {
  const $ = _c(28);
  const {
    legend,
    legendHidden: t1,
    required: t2,
    error,
    appearance: t3,
    columns: t4,
    children
  } = t0;
  const legendHidden = t1 === undefined ? false : t1;
  const required = t2 === undefined ? false : t2;
  const appearance = t3 === undefined ? "card" : t3;
  const columns = t4 === undefined ? 1 : t4;
  const errorId = useId();
  const invalid = Boolean(error);
  let t5;
  if ($[0] !== appearance || $[1] !== invalid || $[2] !== required) {
    t5 = {
      appearance,
      invalid,
      required
    };
    $[0] = appearance;
    $[1] = invalid;
    $[2] = required;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const context = t5;
  const t6 = error ? errorId : undefined;
  let t7;
  if ($[4] !== legendHidden || $[5] !== required) {
    t7 = classNames(styles["helios-choice-group__legend"], {
      [styles["helios-choice-group__legend--required"]]: required,
      [styles["helios-choice-group__legend--hidden"]]: legendHidden
    });
    $[4] = legendHidden;
    $[5] = required;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== legend || $[8] !== t7) {
    t8 = <legend className={t7}>{legend}</legend>;
    $[7] = legend;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = styles[`helios-choice-group__options--${appearance}`];
  const t10 = styles[`helios-choice-group__options--columns-${columns}`];
  let t11;
  if ($[10] !== t10 || $[11] !== t9) {
    t11 = classNames(styles["helios-choice-group__options"], t9, t10);
    $[10] = t10;
    $[11] = t9;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== children || $[14] !== t11) {
    t12 = <div className={t11}>{children}</div>;
    $[13] = children;
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== context || $[17] !== t12) {
    t13 = <ChoiceGroupContext.Provider value={context}>{t12}</ChoiceGroupContext.Provider>;
    $[16] = context;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== error || $[20] !== errorId) {
    t14 = error && <span id={errorId} className={styles["helios-choice-group__error"]} role="alert"><Icon name="xmark" className={styles["helios-choice-group__error-icon"]} /><span>{error}</span></span>;
    $[19] = error;
    $[20] = errorId;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== appearance || $[23] !== t13 || $[24] !== t14 || $[25] !== t6 || $[26] !== t8) {
    t15 = <fieldset data-helios-choice-group={appearance} aria-describedby={t6} className={styles["helios-choice-group"]}>{t8}{t13}{t14}</fieldset>;
    $[22] = appearance;
    $[23] = t13;
    $[24] = t14;
    $[25] = t6;
    $[26] = t8;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  return t15;
}

/**
 * One radio or checkbox: a `<label data-helios-choice>` wrapping a native
 * input (the brand accent colour), the optional icon and the label text.
 * The checked card is drawn in CSS from the input's state, not from a prop.
 * Outside a ChoiceGroup it is a card.
 */
const ChoiceGroupOption = forwardRef<HTMLInputElement, ChoiceGroupOptionProps>(function ChoiceGroupOption(t0, ref) {
  const $ = _c(29);
  let description;
  let icon;
  let label;
  let props;
  let type;
  if ($[0] !== t0) {
    const {
      type: t1,
      label: t2,
      description: t3,
      icon: t4,
      className: _className,
      style: _style,
      ...t5
    } = t0;
    type = t1;
    label = t2;
    description = t3;
    icon = t4;
    props = t5;
    $[0] = t0;
    $[1] = description;
    $[2] = icon;
    $[3] = label;
    $[4] = props;
    $[5] = type;
  } else {
    description = $[1];
    icon = $[2];
    label = $[3];
    props = $[4];
    type = $[5];
  }
  const group = useContext(ChoiceGroupContext);
  const appearance = group?.appearance ?? "card";
  const ariaInvalid = props["aria-invalid"] ?? (group?.invalid ? true : undefined);
  const required = props.required ?? (group?.required && type === "radio" ? true : undefined);
  const t1 = styles[`helios-choice--${appearance}`];
  let t2;
  if ($[6] !== t1) {
    t2 = classNames(styles["helios-choice"], t1);
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== ariaInvalid || $[9] !== props || $[10] !== ref || $[11] !== required || $[12] !== type) {
    t3 = <input {...props} ref={ref} type={type} aria-invalid={ariaInvalid} required={required} className={styles["helios-choice__input"]} />;
    $[8] = ariaInvalid;
    $[9] = props;
    $[10] = ref;
    $[11] = required;
    $[12] = type;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== icon) {
    t4 = icon && <span aria-hidden="true" className={styles["helios-choice__icon"]}>{icon}</span>;
    $[14] = icon;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== label) {
    t5 = <span className={styles["helios-choice__label"]}>{label}</span>;
    $[16] = label;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== description) {
    t6 = description && <span className={styles["helios-choice__description"]}>{description}</span>;
    $[18] = description;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t5 || $[21] !== t6) {
    t7 = <span className={styles["helios-choice__text"]}>{t5}{t6}</span>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== appearance || $[24] !== t2 || $[25] !== t3 || $[26] !== t4 || $[27] !== t7) {
    t8 = <label data-helios-choice={appearance} className={t2}>{t3}{t4}{t7}</label>;
    $[23] = appearance;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
});
const ChoiceGroup = Object.assign(ChoiceGroupRoot, {
  Option: ChoiceGroupOption
});
export default ChoiceGroup;